/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useState } from "react";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import VuiSwitch from "components/VuiSwitch";
import GradientBorder from "examples/GradientBorder";

// Vision UI Dashboard assets
import radialGradient from "assets/theme/functions/radialGradient";
import palette from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";

// Authentication layout components
import CoverLayout from "layouts/authentication/components/CoverLayout";

// Images
import bgSignIn from "assets/images/signInImage.png";

// Firebase authentication
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { auth } from "../../../firebase";
import { IoMdAlert } from "react-icons/io";

function SignIn() {
  // Local developer convenience: when ?demo=true (or window.__LEDGERHUB_DEMO) is set,
  // redirect from the sign-in page to the demo dashboard so it's easy to preview.
  try {
    const demo = typeof window !== 'undefined' && (window.__LEDGERHUB_DEMO || new URLSearchParams(window.location.search).get('demo') === 'true');
    if (demo) {
      window.location.replace('/dashboard?demo=true');
    }
  } catch (e) {
    // ignore
  }
  const [rememberMe, setRememberMe] = useState(() => {
    return localStorage.getItem("rememberMe") === "true";
  });
  const [email, setEmail] = useState(() => {
    return localStorage.getItem("rememberedEmail") || "";
  });
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSetRememberMe = () => {
    setRememberMe((prev) => {
      const newValue = !prev;
      localStorage.setItem("rememberMe", newValue);
      if (!newValue) localStorage.removeItem("rememberedEmail");
      return newValue;
    });
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError("");
    try {
      // Set Firebase session persistence based on "Remember me"
      // browserLocalPersistence → survives browser close (stays signed in)
      // browserSessionPersistence → clears when tab/browser is closed
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      await signInWithEmailAndPassword(auth, email, password);
      if (rememberMe) {
        localStorage.setItem("rememberedEmail", email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }
      window.location.replace("/");
    } catch (err) {
      setError(getFriendlyErrorMessage(err.message || err.code || "Sign-in failed."));
    } finally {
      setIsSubmitting(false);
    }
  };

  function getFriendlyErrorMessage(error) {
    if (!error) return "";
    if (error.includes("auth/user-not-found")) return "No account found with this email.";
    if (error.includes("auth/wrong-password")) return "Incorrect password. Please try again.";
    if (error.includes("auth/invalid-email")) return "Please enter a valid email address.";
    if (error.includes("auth/too-many-requests") || error.toLowerCase().includes("temporarily blocked")) return "Too many failed attempts — your account has been temporarily locked. Please wait a few minutes and try again, or reset your password.";
    if (error.includes("auth/invalid-credential") || error.includes("auth/invalid-login-credentials")) return "Incorrect email or password.";
    if (error.includes("auth/network-request-failed")) return "Network error. Please check your connection.";
    return error.replace("Firebase:", "").replace("(auth/", "").replace(")", "").trim();
  }

  return (
    <CoverLayout
      title="Nice to see you!"
      color="white"
      description="Enter your email and password to sign in"
      premotto="INSPIRED BY THE AI:"
      motto="LEDGERHUB LTD ACCOUNTING"
      image={bgSignIn}
    >
      <VuiBox component="form" role="form" onSubmit={handleSignIn}>
        {error && (
          <VuiBox mb={3} p={2} borderRadius={2} bgcolor="error.main" display="flex" alignItems="center" gap={2} boxShadow={3} role="alert" aria-live="assertive">
            <IoMdAlert color="#fff" size={28} style={{ flexShrink: 0 }} aria-hidden="true" />
            <VuiTypography color="white" fontWeight="bold" sx={{ flex: 1, fontSize: 16 }}>
              {getFriendlyErrorMessage(error)}
            </VuiTypography>
          </VuiBox>
        )}
        <VuiBox mb={2}>
          <VuiBox mb={1} ml={0.5}>
            <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
              Email
            </VuiTypography>
          </VuiBox>
          <GradientBorder
            minWidth="100%"
            padding="1px"
            borderRadius={borders.borderRadius.lg}
            backgroundImage={radialGradient(
              palette.gradients.borderLight.main,
              palette.gradients.borderLight.state,
              palette.gradients.borderLight.angle
            )}
          >
            <VuiInput
              type="email"
              placeholder="Your email..."
              fontWeight="500"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoFocus
            />
          </GradientBorder>
        </VuiBox>
        <VuiBox mb={2}>
          <VuiBox mb={1} ml={0.5}>
            <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
              Password
            </VuiTypography>
          </VuiBox>
          <GradientBorder
            minWidth="100%"
            borderRadius={borders.borderRadius.lg}
            padding="1px"
            backgroundImage={radialGradient(
              palette.gradients.borderLight.main,
              palette.gradients.borderLight.state,
              palette.gradients.borderLight.angle
            )}
          >
            <VuiInput
              type="password"
              placeholder="Your password..."
              sx={({ typography: { size } }) => ({
                fontSize: size.sm,
              })}
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
          </GradientBorder>
        </VuiBox>
        <VuiBox display="flex" alignItems="center">
          <VuiSwitch color="info" checked={rememberMe} onChange={handleSetRememberMe} />
          <VuiTypography
            variant="caption"
            color="white"
            fontWeight="medium"
            onClick={handleSetRememberMe}
            sx={{ cursor: "pointer", userSelect: "none" }}
          >
            &nbsp;&nbsp;&nbsp;&nbsp;Remember me
          </VuiTypography>
        </VuiBox>
        <VuiBox mt={4} mb={1}>
          <VuiButton color="info" fullWidth type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Signing In..." : "SIGN IN"}
          </VuiButton>
        </VuiBox>

        <VuiBox mt={2} mb={1}>
          <VuiButton
            color="white"
            fullWidth
            variant="outlined"
            onClick={() => {
              try {
                const API_BASE = (process.env.REACT_APP_ACCOUNTING_API_BASE_URL || "http://localhost:4001").replace(/\/$/, "");
                // Start the Azure SAML SSO flow on the backend. Support API_BASE values that may already include /api/v1.
                const tenant = (process.env.REACT_APP_SSO_TENANT || "clearbook-advisory");
                const redirect = encodeURIComponent(window.location.origin + "/");
                const loginPath = API_BASE.endsWith("/api/v1") ? `${API_BASE}/sso/azure-login` : `${API_BASE}/api/v1/sso/azure-login`;
                window.location.href = `${loginPath}?tenant=${encodeURIComponent(tenant)}&redirect=${redirect}`;
              } catch (e) {
                // Fallback: navigate to the simple API path with tenant
                window.location.href = `/api/v1/sso/azure-login?tenant=clearbook-advisory`;
              }
            }}
          >
            SIGN IN WITH SSO
          </VuiButton>
        </VuiBox>
      </VuiBox>
    </CoverLayout>
  );
}

export default SignIn;

